import React, { useState } from 'react';
import { Download, FileSpreadsheet, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useRole } from '@/store/userStore';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ExportButtons({ tipo, filters = {} }) {
  const [loading, setLoading] = useState(null);
  const { canExport } = useRole();
  if (!canExport) return null;

  const buildQS = () => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p.toString() ? `?${p.toString()}` : '';
  };

  const downloadFile = (format) => {
    const token = localStorage.getItem("valoriza_token");
    if (!token) {
      toast.error('Tu sesión ha expirado. Vuelve a iniciar sesión para exportar.');
      return;
    }
    setLoading(format);
    try {
      const path = format === 'excel'
        ? `/api/export/excel/${tipo.replace('_', '-')}${buildQS()}`
        : `/api/export/pdf/${tipo.replace('_', '-')}${buildQS()}`;
      const sep = path.includes('?') ? '&' : '?';
      const url = `${API}${path}${sep}token=${encodeURIComponent(token)}`;
      // Native browser navigation, NOT fetch+blob: the browser's own download
      // manager handles the transfer, so it survives whatever was causing
      // exports to silently hang (proxy buffering / long JS fetch timing out
      // with no visible error). If something's wrong (expired token, error),
      // the browser shows the raw response instead of nothing happening.
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`Generando informe ${format === 'excel' ? 'Excel' : 'PDF'}… revisa las descargas de tu navegador`);
    } catch (err) {
      toast.error('Error al exportar el informe');
    } finally {
      setTimeout(() => setLoading(null), 800);
    }
  };

  const hasPDF = ['ops', 'liderazgo', 'near_misses'].includes(tipo); // PDF export available for all data types

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 font-montserrat text-xs h-8"
          style={{ borderColor: 'rgba(74,158,255,0.3)', color: 'var(--vz-text-secondary)', background: 'rgba(74,158,255,0.06)' }}
          disabled={!!loading}
          data-testid={`export-button-${tipo}`}
        >
          {loading ? (
            <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            <Download size={13} />
          )}
          Exportar
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-48"
        style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)' }}
      >
        <DropdownMenuLabel className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Formato de exportación</DropdownMenuLabel>
        <DropdownMenuSeparator style={{ background: 'var(--vz-border-subtle)' }} />
        <DropdownMenuItem
          onClick={() => downloadFile('excel')}
          className="gap-2 cursor-pointer"
          style={{ color: 'var(--vz-text-secondary)' }}
          data-testid={`export-excel-${tipo}`}
        >
          <FileSpreadsheet size={14} className="text-green-400" />
          Excel (.xlsx)
        </DropdownMenuItem>
        {hasPDF && (
          <DropdownMenuItem
            onClick={() => downloadFile('pdf')}
            className="gap-2 cursor-pointer"
            style={{ color: 'var(--vz-text-secondary)' }}
            data-testid={`export-pdf-${tipo}`}
          >
            <FileText size={14} className="text-red-400" />
            PDF (resumen)
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
